'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva } from 'class-variance-authority';
import { Check } from 'lucide-react';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';

import cn from '../lib/cn';

export const checkboxVariants = cva('', {
  variants: {
    size: {
      sm: 'h-[15px] w-[15px] [&_svg]:h-[10px] [&_svg]:w-[10px]',
      lg: 'h-[18px] w-[18px] [&_svg]:h-[12px] [&_svg]:w-[12px]',
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: '',
    },
  },
});

export type CheckboxProps = ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> & {
  size?: 'sm' | 'lg';
  className?: string;
  classNameCheckbox?: string;
  classNameLabel?: string;
};

const Checkbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      classNameCheckbox,
      classNameLabel,
      size = 'lg',
      disabled = false,
      children,
      ...props
    },
    ref,
  ) => (
    <label
      className={cn(
        'flex items-center gap-2',
        checkboxVariants({ disabled }),
        className,
      )}
    >
      <CheckboxPrimitive.Root
        ref={ref}
        disabled={disabled}
        className={cn(
          `flex items-center justify-center peer shrink-0 rounded-[2px] border-2 border-content-tertiary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-none`,
          checkboxVariants({ size }),
          classNameCheckbox,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="block">
          <Check strokeWidth={4} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {children && <div className={classNameLabel}>{children}</div>}
    </label>
  ),
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export default Checkbox;
